import {
	ContentStreamDTO,
	ContentStreamLevelDTO,
	ContentStreamBillingConfigurationDTO,
	ContentStreamBillingConfigurationClient,
	StreamStatsDTO,
	ContentStreamClient,
	ContentStreamSubscriberDTO,
	ContentStreamStoreClient,
	ContentStreamSubscriptionStoreDTO,
	CBSubscriptionStatus,
	OwnerPaymentDTO,
} from "@/Generated/ParticaAPI";
import { ParticaListContent, ParticaListLineContent } from "@/Models/ParticaListModel";

export interface IContentStreamService {
	getContentStreams(customerAccountId: number): Promise<StreamStatsDTO[]>;
	saveContentStream(data: ContentStreamDTO): Promise<ContentStreamDTO>;
	getContentStream(contentStreamId: number): Promise<ContentStreamDTO>;
	saveContentStreamLevel(level: ContentStreamLevelDTO): Promise<ContentStreamLevelDTO>;
	getContentStreamBillingConfig(contentStreamId: number): Promise<ContentStreamBillingConfigurationDTO>;
	saveContentStreamBillingConfig(data: ContentStreamBillingConfigurationDTO): Promise<ContentStreamBillingConfigurationDTO>;
	getContentStreamLevel(levelId: number): Promise<ContentStreamLevelDTO>;
	getNextBillingDay(stream: ContentStreamDTO): string;
	getLatestIssueDate(stream: ContentStreamDTO): string;
	GetAllContentStreams(): Promise<StreamStatsDTO[]>;
	getNextBillingDate(day: number): string;
	getContentStreamSubscriptions(contentStreamId: number): Promise<ContentStreamSubscriberDTO[]>;
	CancelSubscription(subscriberId: number): Promise<any>;
	getSubscriptionsForAccount(customerAccountId: number): Promise<ParticaListContent>;
	DeleteLevel(level: ContentStreamLevelDTO): Promise<any>;
	MoveStream(streamId: number, accountId: number): Promise<any>;
	DeleteStream(streamId: number): Promise<any>;
}

export class ContentStreamService implements IContentStreamService {

	private svc: ContentStreamClient = new ContentStreamClient();
	private csbillingsvc: ContentStreamBillingConfigurationClient = new ContentStreamBillingConfigurationClient();
	private storesvc: ContentStreamStoreClient = new ContentStreamStoreClient();

	public async DeleteStream(streamId: number): Promise<any> {
		return await this.svc.deleteContentStream(streamId);
	}

	public async MoveStream(streamId: number, accountId: number): Promise<any> {
		return await this.svc.moveContentStream(streamId, accountId);
	}

	public async DeleteLevel(level: ContentStreamLevelDTO): Promise<any> {
		return await this.svc.deleteContentStreamLevel(level.contentStreamId as number, level.id as number);
	}

	public async GetAllContentStreams(): Promise<StreamStatsDTO[]> {
		return await this.svc.getAllContentStreams();
	}
	public async CancelSubscription(subscriberId: number): Promise<any> {
		return await this.storesvc.cancelSubscription(subscriberId);
	}


	public getNextBillingDay(stream: ContentStreamDTO): string {
		if (stream.contentStreamBillingConfigurations === undefined || stream.contentStreamBillingConfigurations.length === 0) {
			return "Unknown, billing not setup";
		}
		const billingDay = stream.contentStreamBillingConfigurations[0].billingDay;
		return this.getNextBillingDate(billingDay);
	}

	public getNextBillingDate(day: number): string {
		const today: Date = new Date();
		if (today.getDate() <= day) {
			const billingDay = new Date(today.getFullYear(), today.getMonth(), day);
			return billingDay.toDateString();
		} else {
			const billingDay = new Date(today.getFullYear(), today.getMonth() + 1, day);
			return billingDay.toDateString();
		}
	}

	public getLatestIssueDate(stream: ContentStreamDTO): string {
		if (stream !== undefined && stream.publication !== undefined && stream.publication.issues !== undefined) {
			if (stream.publication.issues.length === 0) {
				return "";
			}
			const latest: Date = stream.publication.issues.slice(0).sort(
				(a, b) => (b.id as number) - (a.id as number)
			)[0].issueDate as Date;
			return latest.toDateString();
		} else {
			return "";
		}
	}


	public async getContentStreamBillingConfig(contentStreamId: number): Promise<ContentStreamBillingConfigurationDTO> {
		return await this.csbillingsvc.get(contentStreamId);
	}

	public async saveContentStreamBillingConfig(data: ContentStreamBillingConfigurationDTO):
		Promise<ContentStreamBillingConfigurationDTO> {
		if (data.id && data.id >= 0) {
			return await this.csbillingsvc.update(data);
		} else {
			data.id = 0;
			return await this.csbillingsvc.add(data);
		}
	}
	public async saveContentStream(data: ContentStreamDTO): Promise<ContentStreamDTO> {
		data.publication = undefined;
		if (data.id === 0) {
			return this.svc.saveContentStream(data);
		} else {
			await this.saveContentStreamBillingConfig(data.contentStreamBillingConfigurations[0]);
			return this.svc.saveContentStream(data);
		}
	}
	public async getContentStreams(customerAccountId: number): Promise<StreamStatsDTO[]> {
		return this.svc.getContentStreams(customerAccountId);
	}
	public async getContentStream(contentStreamId: number): Promise<ContentStreamDTO> {
		return this.svc.getContentStream(contentStreamId);
	}
	public async saveContentStreamLevel(level: ContentStreamLevelDTO): Promise<ContentStreamLevelDTO> {
		return this.svc.saveContentStreamLevel(level);
	}
	public async getContentStreamLevel(levelId: number): Promise<ContentStreamLevelDTO> {
		return this.svc.getContentStreamLevel(levelId);
	}

	public async getContentStreamSubscriptions(contentStreamId: number): Promise<ContentStreamSubscriberDTO[]> {
		return await this.svc.getContentStreamSubscriptions(contentStreamId);
	}

	public async getSubscriptionsForAccount(customerAccountId: number): Promise<ParticaListContent> {
		const subs: ContentStreamSubscriptionStoreDTO[] = await this.storesvc.getContentStreamSubscriptions(customerAccountId);
		const data: ParticaListContent = new ParticaListContent();
		data.title = "Your Subscriptions.";
		subs.forEach(sub => {
			const line: ParticaListLineContent = new ParticaListLineContent();
			line.title = sub.name as string;
			line.id = (sub.id as number).toString();
			line.subtitle = (sub.subscriptionStatus as CBSubscriptionStatus).status as string;
			data.lines.push(line);
		});
		return data;
	}
}
